import {
	ChangeTriggerKind,
	ContextActionBar,
	FloatingActionButton,
	OptionsMenu,
	SearchInputForm,
	SearchPlaceholder,
	ToolbarAction,
	ToolbarRegularAction,
	getPropertySortText
} from "client/Components";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { useFollowCategoryAction } from "../../Sidebar/useFollowCategoryAction";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import {
	ActiveFilter,
	getFollowCategorySubscriptionStatus,
	HubDialogContext,
	HubItemIsNewFilter
} from "../../common";
import { EventDateFilterType, EventPreviewModel } from "client/Api/Hub/Events/eventViewer";
import { CardOverview } from "../CardOverview/CardOverview";
import { HubOverviewLayout } from "../../HubOverviewLayout";
import {
	MediaQueryKind,
	useForceRerender,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { DataSourceSortDirection, DataSourceSortableState } from "client/Data/sorting";
import {
	ActionKind,
	buildActionsForTopBar
} from "client_legacy/ndaw/actions";
import { TimeZone } from "client/DateAndTime/timezones";
import { useHubManagementDialog } from "../../Management/HubManagementDialog/HubManagementDialog";
import { CreateHubItemDialog } from "../../CreateHubItemDialog/CreateHubItemDialog";
import { isNotNil } from "client/Misc/utils";
import { FilterMenu } from "./FilterMenu";
import { useHubItemsChangedListener } from "../../useHubItemsChangedListener";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type OverviewProps = {
	activeFilter: ActiveFilter | null;
	searchTerm: VersionedValue<string>;
	selectedCategoryId: number | null;
	categories: readonly HubItemCategoryModel[];
	sorting: DataSourceSortableState<EventPreviewModel>;
	dateFilter: EventDateFilterType;
	hubItemKindsCanCreateNewCategoriesFor: ReadonlyArray<HubItemKind>;
	onActiveFilterChange: (filter: ActiveFilter | null) => void;
	getCategory: (id: number | nil) => HubItemCategoryModel | undefined;
	onSelectedCategoryIdChange: (id: number) => void;
	onCategoryCreated: () => void;
	onNavigateToItem(id: number): void;
	onSearchTermChange: (value: VersionedValue<string>) => void;
	onDateSortingChange: (value: DataSourceSortableState<EventPreviewModel>) => void;
	onDateFilterChange: (value: EventDateFilterType) => void;
	canOpenHubManagement: boolean;
	isNewFilter: HubItemIsNewFilter;
	onIsNewFilterChange: (value: HubItemIsNewFilter) => void;
};

export const Overview = (props: OverviewProps): ReactElement => {
	const {
		activeFilter,
		onActiveFilterChange,
		selectedCategoryId,
		onSelectedCategoryIdChange,
		categories,
		getCategory,
		onCategoryCreated,
		onNavigateToItem,
		searchTerm,
		onSearchTermChange,
		sorting,
		dateFilter,
		onDateSortingChange,
		onDateFilterChange,
		hubItemKindsCanCreateNewCategoriesFor,
		canOpenHubManagement,
		isNewFilter,
		onIsNewFilterChange
	} = props;

	const [shouldShowNewEventDialog, setShouldShowNewEventDialog] = useState<boolean>(false);

	const { setViewSpecificHandlers } = useContext(HubDialogContext);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const userTimezone: TimeZone = useUserTimezone();
	const [reloadData, dataVersion] = useForceRerender();
	const [hubManagementDialog, openHubManagementDialog] = useHubManagementDialog();

	useHubItemsChangedListener(
		reloadData,
		HubItemKind.Event
	);

	const hasCreatePermission: boolean = useMemo(
		() => {
			const canCreateNewCategory: boolean = hubItemKindsCanCreateNewCategoriesFor
				.includes(HubItemKind.Event);

			return canCreateNewCategory
				|| categories.some(
					x => x.hubItemKind === HubItemKind.Event
						&& x.canCreateItem
				);
		},
		[categories, hubItemKindsCanCreateNewCategoriesFor]
	);

	const canCreateEvent: boolean = !activeFilter && hasCreatePermission;

	const selectedCategory = useMemo(
		(): HubItemCategoryModel | undefined => getCategory(selectedCategoryId),
		[getCategory, selectedCategoryId]
	);

	const preferredCategoryId: number | undefined = useMemo(
		() => {
			return categories
				.find(x => (x.id === selectedCategoryId && x.canCreateItem))
				?.id;
		},
		[categories, selectedCategoryId]
	);

	const isFollowing = useMemo(
		(): boolean => {
			if (categories.length === 0) {
				return false;
			}
			return selectedCategoryId === null
				? false
				: getFollowCategorySubscriptionStatus(categories, selectedCategoryId);
		},
		[categories, selectedCategoryId]
	);

	const createNewHandler = useCallback(
		(): void => {
			setShouldShowNewEventDialog(true);
		},
		[]
	);

	const closeNewEventDialogHandler = useCallback(
		(wasAnyCategoryCreated: boolean): void => {
			setShouldShowNewEventDialog(false);

			if (wasAnyCategoryCreated) {
				onCategoryCreated();
			}
		},
		[onCategoryCreated]
	);

	const followAction: ToolbarAction<EventActionContext> = useFollowCategoryAction<EventActionContext>(
		selectedCategoryId,
		isFollowing
	);

	const defaultActions = useMemo(
		(): readonly EventAction[] => ([
			{
				kind: ActionKind.Regular,
				name: "manage-event",
				title: localize("_Manage"),
				icon: "gear",
				canExecute: (x): boolean => x.canOpenHubManagement,
				execute: () => {
					openHubManagementDialog({
						initialView: "event.overview"
					});
				}
			},
			followAction
		]),
		[
			followAction,
			openHubManagementDialog
		]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<EventActionContext> => {
			return getNewAction(createNewHandler);
		},
		[createNewHandler]
	);

	const topBarActions = useMemo(
		(): readonly EventAction[] => {
			return buildActionsForTopBar<EventActionContext, EventAction>(
				{
					defaultActions,
					mainActions: [mainAction]
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive,
			mainAction
		]
	);

	useEffect(
		(): (() => void) => {
			const backHandler: (() => void) | undefined = activeFilter
				? () => onActiveFilterChange(null)
				: undefined;

			setViewSpecificHandlers({
				backHandler
			});

			return () => {
				setViewSpecificHandlers(undefined);
			};
		},
		[
			setViewSpecificHandlers,
			activeFilter,
			onActiveFilterChange
		]
	);

	const actionContext = useMemo(
		(): EventActionContext => ({
			canCreateEvent,
			canOpenHubManagement,
			isResponsive,
			categoryId: selectedCategoryId
		}),
		[
			canCreateEvent,
			canOpenHubManagement,
			isResponsive,
			selectedCategoryId
		]
	);

	const fabHandler = useMemo(
		(): (() => void) | undefined => {
			if (mainAction.canExecute(actionContext)) {
				return () => mainAction.execute(actionContext);
			}

			return undefined;
		},
		[
			actionContext,
			mainAction
		]
	);

	return (
		<>
			<HubOverviewLayout trackingId="events-overview">
				{isMobile && (
					<ContextActionBar
						actions={topBarActions}
						context={actionContext}
						hasBorderBottom
						trackingId="global-action-bar"
					/>
				)}

				<HubOverviewLayout.Content>
					<HubOverviewLayout.Header
						trackingId="events-overview-header"
						activeFilter={activeFilter}
						onClearActiveFilter={() => onActiveFilterChange(null)}
						title={selectedCategory?.name ?? localize("_AllEvents")}
						actions={topBarActions}
						actionContext={actionContext}
					>
						<SearchInputForm
							value={searchTerm}
							onChange={(value, version) => onSearchTermChange({ value, version })}
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={[
										localize("_Title")
									]}
								/>
							)}
							trackingId="search-input"
							trigger={{ kind: ChangeTriggerKind.Debounced }}
							height="large"
							icon="filter"
						/>
						<FilterMenu
							dateFilter={dateFilter}
							isNewFilter={isNewFilter}
							onIsNewFilterChange={onIsNewFilterChange}
							onDateFilterChange={onDateFilterChange}
							trackingId="filter-on"
						/>
						<OptionsMenu
							title={localize("_SortBy")}
							trackingId="sort-by"
							value={sorting}
							options={sortOptions}
							onChange={onDateSortingChange}
							icon="arrow-up-arrow-down"
							buttonSize="large"
						/>
					</HubOverviewLayout.Header>

					<HubOverviewLayout.SectionContainer
						trackingId="section-container"
					>
						<CardOverview
							key={dataVersion}
							activeFilter={activeFilter}
							sorting={sorting}
							dateFilter={dateFilter}
							searchTerm={searchTerm.value}
							getCategory={getCategory}
							onNavigateToItem={onNavigateToItem}
							onActiveFilterChange={onActiveFilterChange}
							onSelectedCategoryIdChange={onSelectedCategoryIdChange}
							selectedCategoryId={selectedCategoryId}
							shouldReserveSpaceForFloatingActionButton={isNotNil(fabHandler)}
							timezone={userTimezone}
							isNewFilter={isNewFilter}
						/>
						<AiAssistantFabSpacing />
					</HubOverviewLayout.SectionContainer>

					{fabHandler && (
						<FloatingActionButton
							icon="plus"
							onClick={fabHandler}
							trackingId="create-event-mobile-button"
						/>
					)}
				</HubOverviewLayout.Content>
			</HubOverviewLayout>
			{shouldShowNewEventDialog && (
				<CreateHubItemDialog
					kind={HubItemKind.Event}
					preferredCategoryId={preferredCategoryId}
					onSaveHubItem={reloadData}
					onClose={closeNewEventDialogHandler}
					onBack={closeNewEventDialogHandler}
				/>
			)}
			{hubManagementDialog}
		</>
	);
};
Overview.displayName = "Overview";

type SortByOption = {
	value: DataSourceSortableState<EventPreviewModel>;
	text: string;
	valueTrackingId: string;
};


export const sortOptions: readonly SortByOption[] = [
	{
		value: { propertyName: "startDate", direction: DataSourceSortDirection.Ascending },
		text: getPropertySortText("_EventDate", "_NearFar"),
		valueTrackingId: "event-date-near-far"
	},
	{
		value: { propertyName: "startDate", direction: DataSourceSortDirection.Descending },
		text: getPropertySortText("_EventDate", "_FarNear"),
		valueTrackingId: "event-date-far-near"
	}
];

type FilterByOption = {
	value: EventDateFilterType;
	text: string;
	valueTrackingId: string;
};

export const dateFilterOptions: readonly FilterByOption[] = [
	{
		value: EventDateFilterType.Upcoming,
		text: localize("_UpcomingEvents"),
		valueTrackingId: "upcoming-events"
	},
	{
		value: EventDateFilterType.Past,
		text: localize("_PastEvents"),
		valueTrackingId: "past-events"
	}
];

type EventActionContext = {
	canCreateEvent: boolean;
	canOpenHubManagement: boolean;
	isResponsive: boolean;
	categoryId: number | null;
};

type EventAction = ToolbarAction<EventActionContext>;

function getNewAction(
	handler: () => void
): ToolbarRegularAction<EventActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "create-event",
		title: localize("_New"),
		icon: "plus",
		isPrimary: true,
		canExecute: (x): boolean => x.canCreateEvent,
		execute: (): void => {
			handler();
		}
	};
}